import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../hooks/useAuth';
import { LeadTask } from '../types';

export interface RefreshTasksOptions {
    companyId?: number | null;
    userId?: string | null;
    funnelId?: number | null;
}

interface TasksContextType {
    tasks: LeadTask[];
    loading: boolean;
    refreshTasks: (options?: RefreshTasksOptions) => Promise<void>;
    addTask: (task: LeadTask) => void;
    updateTaskInQueue: (taskId: number, updates: Partial<LeadTask>) => void;
    removeTask: (taskId: number) => void;
}

const TasksContext = createContext<TasksContextType>({} as TasksContextType);

// Sort helper: Overdue first, then Today, then Upcoming
const sortTasks = (tasks: LeadTask[]) => {
    return tasks.sort((a, b) => {
        const dateA = new Date(a.due_date || 0).getTime();
        const dateB = new Date(b.due_date || 0).getTime();
        return dateA - dateB;
    });
};

export function TasksProvider({ children }: { children: ReactNode }) {
    const { user, isSuperAdmin } = useAuth();
    const [tasks, setTasks] = useState<LeadTask[]>([]);
    const [loading, setLoading] = useState(false);

    const refreshTasks = useCallback(async (options?: RefreshTasksOptions) => {
        if (!supabase || !user) return;
        setLoading(true);
        try {
            const targetCompanyId = options?.companyId ?? user.company_id;
            const targetUserId = options?.userId;
            const targetFunnelId = options?.funnelId;

            let query = supabase
                .from('lead_tasks')
                .select(`
                    *,
                    users(name),
                    leads!inner(id, name, company_id, funnel_id)
                `)
                .eq('status', 'pending');

            if (targetCompanyId) {
                query = query.eq('leads.company_id', targetCompanyId);
            }

            if (isSuperAdmin) {
                if (targetUserId && targetUserId !== 'all') {
                    query = query.eq('assigned_to', targetUserId);
                }
            } else if (user.role !== 'gestor') {
                query = query.eq('assigned_to', user.id);
            }

            const { data, error } = await query;
            if (error) {
                // Fallback query simple
                let fallbackQuery = supabase
                    .from('lead_tasks')
                    .select(`
                        *,
                        users(name),
                        leads(id, name, company_id, funnel_id)
                    `)
                    .eq('status', 'pending');

                if (isSuperAdmin) {
                    if (targetUserId && targetUserId !== 'all') {
                        fallbackQuery = fallbackQuery.eq('assigned_to', targetUserId);
                    }
                } else if (user.role !== 'gestor') {
                    fallbackQuery = fallbackQuery.eq('assigned_to', user.id);
                }

                const { data: fallbackData, error: fallbackError } = await fallbackQuery;
                if (fallbackError) throw fallbackError;

                let result = (fallbackData as LeadTask[]) || [];
                if (targetCompanyId) {
                    result = result.filter(t => (t as any).leads?.company_id === targetCompanyId);
                }
                if (targetFunnelId) {
                    result = result.filter(t => {
                        const l = (t as any).leads;
                        return l ? (l.funnel_id ? l.funnel_id === targetFunnelId : true) : true;
                    });
                }
                setTasks(sortTasks(result));
            } else {
                let result = (data as LeadTask[]) || [];
                if (targetFunnelId) {
                    result = result.filter(t => {
                        const l = (t as any).leads;
                        return l ? (l.funnel_id ? l.funnel_id === targetFunnelId : true) : true;
                    });
                }
                setTasks(sortTasks(result));
            }
        } catch (error) {
            console.error('Error fetching global tasks:', error);
        } finally {
            setLoading(false);
        }
    }, [user, isSuperAdmin]);

    // Initial fetch when user loads
    useEffect(() => {
        if (user) refreshTasks();
    }, [user, refreshTasks]);

    const addTask = useCallback((task: LeadTask) => {
        setTasks(prev => sortTasks([...prev, task]));
    }, []);

    const updateTaskInQueue = useCallback((taskId: number, updates: Partial<LeadTask>) => {
        setTasks(prev => {
            // Apply updates
            const updated = prev.map(t => t.id === taskId ? { ...t, ...updates } : t);

            // If status changed to completed/skipped, remove from queue?
            // The queue usually only shows pending. 
            // Let's filter if status is not pending.
            if (updates.status && updates.status !== 'pending') {
                return updated.filter(t => t.id !== taskId);
            }

            return sortTasks(updated);
        });
    }, []);

    const removeTask = useCallback((taskId: number) => {
        setTasks(prev => prev.filter(t => t.id !== taskId));
    }, []);

    return (
        <TasksContext.Provider value={{ tasks, loading, refreshTasks, addTask, updateTaskInQueue, removeTask }}>
            {children}
        </TasksContext.Provider>
    );
}

export function useTasksContext() {
    return useContext(TasksContext);
}
